import { useEffect, useRef, useState } from 'react';
import { Container, Stack, Title, Text, Button, Center, ThemeIcon, Loader } from '@mantine/core';
import { IconCheck, IconArrowRight } from '@tabler/icons-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { notifications } from '@mantine/notifications';
import { usePageTitle } from '../hooks/usePageTitle';
import { useSubscription } from '../hooks/useSubscription';
import { useAuth } from '../hooks/useAuth';
import { verifyCheckoutSession } from '../services/subscriptionService';
import { trackSubscriptionReddit } from '../utils/redditPixel';
import { trackSubscriptionMeta } from '../utils/metaPixel';
import { PLAN_PRICES } from '../constants/pricing';

const MAX_VERIFY_ATTEMPTS = 5;
const VERIFY_RETRY_INTERVAL_MS = 2000;

export const SubscriptionSuccessPage = () => {
  usePageTitle('Subscription Confirmed | Meals You Love');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { refetch, subscription } = useSubscription();
  const { user } = useAuth();
  const sessionId = searchParams.get('session_id');
  const hasStartedVerification = useRef(false);
  const hasTrackedPurchase = useRef(false);
  const [verifying, setVerifying] = useState(!!sessionId);

  useEffect(() => {
    if (!sessionId || !user || hasStartedVerification.current) return;
    hasStartedVerification.current = true;

    let cancelled = false;

    const verify = async () => {
      for (let attempt = 0; attempt < MAX_VERIFY_ATTEMPTS; attempt++) {
        if (cancelled) return;

        try {
          const token = await user.getIdToken();
          const result = await verifyCheckoutSession(sessionId, token);

          if (result.verified) {
            await refetch();
            if (!cancelled) {
              setVerifying(false);
              notifications.show({
                title: 'Subscription activated!',
                message: 'Welcome to Meals You Love! Your subscription is now active.',
                color: 'green',
                icon: <IconCheck size={18} />,
              });
            }
            return;
          }
        } catch (error) {
          console.error('Error verifying checkout session:', error);
        }

        // Wait before retrying
        if (attempt < MAX_VERIFY_ATTEMPTS - 1) {
          await new Promise((resolve) => setTimeout(resolve, VERIFY_RETRY_INTERVAL_MS));
        }
      }

      // Exhausted retries — fall back to refetch and hope webhook arrived
      await refetch();
      if (!cancelled) {
        setVerifying(false);
        notifications.show({
          title: 'Subscription activated!',
          message: 'Welcome to Meals You Love! Your subscription is now active.',
          color: 'green',
          icon: <IconCheck size={18} />,
        });
      }
    };

    verify();

    return () => {
      cancelled = true;
    };
  }, [sessionId, user, refetch]);

  // Track subscription purchase once we have the subscription details
  useEffect(() => {
    if (
      sessionId &&
      !hasTrackedPurchase.current &&
      user &&
      subscription &&
      subscription.status === 'active' &&
      subscription.stripeSubscriptionId &&
      // Only track when we know the actual purchasable plan, so we never
      // fall back to a guessed value (which collapses every Purchase event
      // onto the same price). Lifetime is excluded since it's not purchasable.
      (subscription.planType === 'monthly' || subscription.planType === 'annual')
    ) {
      hasTrackedPurchase.current = true;

      const value = PLAN_PRICES[subscription.planType];

      trackSubscriptionReddit(
        user.uid,
        subscription.stripeSubscriptionId,
        value
      );
      trackSubscriptionMeta(
        user.uid,
        subscription.stripeSubscriptionId,
        value
      );
    }
  }, [sessionId, user, subscription]);

  return (
    <Container size="sm" py="xl">
      <Stack gap="xl" align="center">
        <Center>
          {verifying ? (
            <Loader size="lg" />
          ) : (
            <ThemeIcon size={80} radius="xl" variant="light" color="green">
              <IconCheck size={40} />
            </ThemeIcon>
          )}
        </Center>

        <Stack gap="md" align="center">
          <Title order={1} ta="center">
            {verifying ? 'Activating your subscription...' : 'Welcome to Meals You Love!'}
          </Title>
          <Text size="lg" c="dimmed" ta="center">
            {verifying
              ? 'Please wait while we confirm your payment.'
              : 'Your subscription is now active.'}
          </Text>
        </Stack>

        {!verifying && (
          <>
            <Button
              size="lg"
              rightSection={<IconArrowRight size={18} />}
              onClick={() => navigate('/')}
            >
              Start planning meals
            </Button>

            <Button
              variant="subtle"
              onClick={() => navigate('/billing')}
            >
              View Billing Details
            </Button>
          </>
        )}
      </Stack>
    </Container>
  );
};
